// src/components/Forms/PractitionerOnboardingSteps.jsx
import React, { useState, useEffect } from 'react';
import { useForm } from '../../hooks/useForm';
import FormField from './FormField';
import apiService from '../../services/api';

// Step 1: Basic Information
export const BasicInfoStep = ({ data, onNext, onCancel, isFirstStep, isLastStep }) => {
  const initialValues = {
    fullName: data.fullName || '',
    title: data.title || 'Dr.',
    email: data.email || '',
    phone: data.phone || '',
    dateOfBirth: data.dateOfBirth || '',
    address: data.address || ''
  };

  const validationSchema = {
    fullName: { 
      required: { message: 'Full name is required' },
      minLength: { value: 2, message: 'Name must be at least 2 characters' }
    },
    email: { 
      required: { message: 'Email is required' },
      email: { message: 'Please enter a valid email' }
    },
    phone: { 
      required: { message: 'Phone is required' },
      phone: { message: 'Please enter a valid UK phone number' }
    }
  };

  const { 
    values, 
    errors, 
    touched, 
    isSubmitting,
    handleChange, 
    handleBlur, 
    handleSubmit 
  } = useForm(initialValues, validationSchema);

  const titleOptions = [
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Prof.', label: 'Prof.' }
  ];

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="step-header">
        <h3>Basic Information</h3>
        <p>Let's start with your basic personal details</p>
      </div>

      <div className="form-grid">
        <FormField
          name="fullName"
          label="Full Name"
          value={values.fullName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.fullName}
          touched={touched.fullName}
          required
          placeholder="Dr. John Smith"
        />
        
        <FormField
          type="select"
          name="title"
          label="Title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          options={titleOptions}
        />
        
        <FormField
          type="email"
          name="email"
          label="Email Address"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email}
          touched={touched.email}
          required
          placeholder="doctor@clinic.com"
        />
        
        <FormField
          type="tel"
          name="phone"
          label="Phone Number"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.phone}
          touched={touched.phone}
          required
          placeholder="+44 7123 456789"
        />
        
        <FormField
          type="date"
          name="dateOfBirth"
          label="Date of Birth"
          value={values.dateOfBirth}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      
      <FormField
        name="address"
        label="Home Address"
        value={values.address}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Full home address"
      />

      <div className="step-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Next: Professional Details →'}
        </button>
      </div>
    </form>
  );
};

// Step 2: Professional Details
export const ProfessionalStep = ({ data, onNext, onPrev, onCancel }) => {
  const initialValues = {
    specialization: data.specialization || '',
    licenseNumber: data.licenseNumber || '',
    qualifications: data.qualifications || '',
    experience: data.experience || '',
    bio: data.bio || '',
    languages: data.languages || []
  };

  const validationSchema = {
    specialization: {},
    licenseNumber: { required: { message: 'License number is required' } },
    experience: { 
      required: { message: 'Years of experience is required' },
      custom: (value) => {
        if (value && (isNaN(value) || parseInt(value) < 0 || parseInt(value) > 50)) {
          return 'Please enter a valid number of years (0-50)';
        }
        return null;
      }
    }
  };

  const { 
    values, 
    errors, 
    touched, 
    isSubmitting,
    handleChange, 
    handleBlur, 
    handleSubmit 
  } = useForm(initialValues, validationSchema);

  const specializationOptions = [
    { value: 'Physiotherapy', label: 'Physiotherapy' },
    { value: 'Osteopathy', label: 'Osteopathy' },
    { value: 'Sports Therapy', label: 'Sports Therapy' },
    { value: 'Massage Therapy', label: 'Massage Therapy' },
    { value: 'Acupuncture', label: 'Acupuncture' },
    { value: 'Chiropractic', label: 'Chiropractic' }
  ];

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="step-header">
        <h3>Professional Details</h3>
        <p>Tell us about your professional background and qualifications</p>
      </div>

      <div className="form-grid">
        <FormField
          type="select"
          name="specialization"
          label="Primary Specialization"
          value={values.specialization}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.specialization}
          touched={touched.specialization}
          required
          options={specializationOptions}
        />
        
        <FormField
          name="licenseNumber"
          label="Professional License Number"
          value={values.licenseNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.licenseNumber}
          touched={touched.licenseNumber}
          required
          placeholder="e.g., HCPC PH123456"
        />
        
        <FormField
          type="number"
          name="experience"
          label="Years of Experience"
          value={values.experience}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.experience}
          touched={touched.experience}
          required
          placeholder="e.g., 5"
          min="0"
          max="50"
        />
      </div>
      
      <FormField
        type="textarea"
        name="qualifications"
        label="Qualifications & Certifications"
        value={values.qualifications}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="List your professional qualifications, certifications, and training..."
        rows={4}
      />
      
      <FormField
        type="textarea"
        name="bio"
        label="Professional Bio"
        value={values.bio}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Brief professional biography for your profile..."
        rows={4}
      />

      <div className="step-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onPrev}
          disabled={isSubmitting}
        >
          ← Previous
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Next: Schedule & Availability →'}
        </button>
      </div>
    </form>
  );
};

// Step 3: Schedule & Availability
export const ScheduleStep = ({ data, onNext, onPrev, onCancel }) => {
  const [availableLocations, setAvailableLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiService.getOffices();
        setAvailableLocations(response || []);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setAvailableLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const initialValues = {
    workingDays: data.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    startTime: data.startTime || '09:00',
    endTime: data.endTime || '17:00',
    lunchBreakStart: data.lunchBreakStart || '12:00',
    lunchBreakEnd: data.lunchBreakEnd || '13:00',
    consultationDuration: data.consultationDuration || '60',
    locationIds: data.locationIds || [],
    availableFromDate: data.availableFromDate || ''
  };

  const validationSchema = {
    availableFromDate: { required: { message: 'Available from date is required' } },
    workingDays: {
      custom: (value) => {
        if (!value || value.length === 0) {
          return 'Please select at least one working day';
        }
        return null;
      }
    }
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit
  } = useForm(initialValues, validationSchema);

  const handleLocationToggle = (locationId) => {
    const currentIds = values.locationIds || [];
    const updated = currentIds.includes(locationId)
      ? currentIds.filter(id => id !== locationId)
      : [...currentIds, locationId];
    handleChange('locationIds', updated);
  };

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '60 minutes' },
    { value: '90', label: '90 minutes' }
  ];

  const timeOptions = [];
  for (let hour = 6; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push({ value: timeString, label: timeString });
    }
  }

  const weekdays = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const handleWorkingDayChange = (day, checked) => {
    const currentDays = values.workingDays || [];
    const updatedDays = checked 
      ? [...currentDays, day]
      : currentDays.filter(d => d !== day);
    handleChange('workingDays', updatedDays);
  };

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="step-header">
        <h3>Schedule & Availability</h3>
        <p>Set up your working schedule and availability preferences</p>
      </div>

      <div className="form-group">
        <label className="form-label">
          Locations
          {values.locationIds.length > 0 && (
            <span className="form-label-count"> ({values.locationIds.length} selected)</span>
          )}
        </label>
        {locationsLoading ? (
          <div className="form-loading-state">Loading locations...</div>
        ) : availableLocations.length === 0 ? (
          <div className="form-empty-state">
            No locations found. Add locations in Practice Management first.
          </div>
        ) : (
          <div className="location-multi-select">
            {availableLocations.map(location => {
              const isSelected = values.locationIds.includes(location.id);
              return (
                <label
                  key={location.id}
                  className={`location-checkbox-item${isSelected ? ' selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleLocationToggle(location.id)}
                  />
                  <span className="location-checkbox-content">
                    <span className="location-checkbox-name">{location.name}</span>
                    {location.addressLine1 && (
                      <span className="location-checkbox-addr">{location.addressLine1}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">
          Working Days *
          {errors.workingDays && touched.workingDays && (
            <span className="required" style={{ color: 'var(--rose)', marginLeft: '8px' }}>
              {errors.workingDays}
            </span>
          )}
        </label>
        <div className="checkbox-grid">
          {weekdays.map(day => (
            <label key={day.value} className="checkbox-field">
              <input 
                type="checkbox"
                checked={values.workingDays?.includes(day.value) || false}
                onChange={(e) => handleWorkingDayChange(day.value, e.target.checked)}
              />
              <span className="checkbox-label">{day.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-grid">
        <FormField
          type="select"
          name="startTime"
          label="Start Time"
          value={values.startTime}
          onChange={handleChange}
          onBlur={handleBlur}
          options={timeOptions}
        />
        
        <FormField
          type="select"
          name="endTime"
          label="End Time"
          value={values.endTime}
          onChange={handleChange}
          onBlur={handleBlur}
          options={timeOptions}
        />
        
        <FormField
          type="select"
          name="lunchBreakStart"
          label="Lunch Break Start"
          value={values.lunchBreakStart}
          onChange={handleChange}
          onBlur={handleBlur}
          options={timeOptions}
        />
        
        <FormField
          type="select"
          name="lunchBreakEnd"
          label="Lunch Break End"
          value={values.lunchBreakEnd}
          onChange={handleChange}
          onBlur={handleBlur}
          options={timeOptions}
        />
        
        <FormField
          type="select"
          name="consultationDuration"
          label="Default Consultation Duration"
          value={values.consultationDuration}
          onChange={handleChange}
          onBlur={handleBlur}
          options={durationOptions}
        />
        
        <FormField
          type="date"
          name="availableFromDate"
          label="Available From"
          value={values.availableFromDate}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.availableFromDate}
          touched={touched.availableFromDate}
          required
          min={new Date().toISOString().split('T')[0]}
        />
      </div>

      <div className="step-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onPrev}
          disabled={isSubmitting}
        >
          ← Previous
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Next: Services & Pricing →'}
        </button>
      </div>
    </form>
  );
};

// Step 4: Services & Pricing
export const ServicesStep = ({ data, onNext, onPrev, onCancel }) => {
  const initialValues = {
    services: data.services || [],
    specialOffers: data.specialOffers || '',
    cancellationPolicy: data.cancellationPolicy || '24',
    paymentMethods: data.paymentMethods || ['card', 'cash']
  };

  const { 
    values, 
    errors, 
    touched, 
    isSubmitting,
    handleChange, 
    handleBlur, 
    handleSubmit 
  } = useForm(initialValues, {});

  const availableServices = [
    { id: 'physio-consultation', name: 'Physiotherapy Consultation', defaultPrice: '80' },
    { id: 'sports-massage', name: 'Sports Massage', defaultPrice: '65' },
    { id: 'osteopathy', name: 'Osteopathy Session', defaultPrice: '85' },
    { id: 'acupuncture', name: 'Acupuncture', defaultPrice: '70' },
    { id: 'chiropractic', name: 'Chiropractic Treatment', defaultPrice: '90' }
  ];

  const handleServiceToggle = (serviceId, enabled) => {
    const currentServices = values.services || [];
    if (enabled) {
      const service = availableServices.find(s => s.id === serviceId);
      const newService = {
        id: serviceId,
        name: service.name,
        price: service.defaultPrice,
        duration: '60'
      };
      handleChange('services', [...currentServices, newService]);
    } else {
      handleChange('services', currentServices.filter(s => s.id !== serviceId));
    }
  };

  const handleServiceUpdate = (serviceId, field, value) => {
    const updatedServices = values.services.map(service => 
      service.id === serviceId ? { ...service, [field]: value } : service
    );
    handleChange('services', updatedServices);
  };

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="step-header">
        <h3>Services & Pricing</h3>
        <p>Select the services you offer and set your pricing</p>
      </div>

      <div className="services-selection">
        <label className="form-label">Services Offered</label>
        {availableServices.map(service => {
          const isSelected = values.services?.some(s => s.id === service.id) || false;
          const selectedService = values.services?.find(s => s.id === service.id);
          
          return (
            <div key={service.id} className="service-item">
              <label className="checkbox-field">
                <input 
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => handleServiceToggle(service.id, e.target.checked)}
                />
                <span className="checkbox-label">{service.name}</span>
              </label>
              
              {isSelected && (
                <div className="service-details">
                  <div className="service-inputs">
                    <input
                      type="number"
                      placeholder="Price (£)"
                      value={selectedService?.price || ''}
                      onChange={(e) => handleServiceUpdate(service.id, 'price', e.target.value)}
                      className="form-input service-price"
                      min="0"
                      step="0.01"
                    />
                    <select
                      value={selectedService?.duration || '60'}
                      onChange={(e) => handleServiceUpdate(service.id, 'duration', e.target.value)}
                      className="form-input service-duration"
                    >
                      <option value="30">30 min</option>
                      <option value="45">45 min</option>
                      <option value="60">60 min</option>
                      <option value="90">90 min</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <FormField
        type="textarea"
        name="specialOffers"
        label="Special Offers or Packages"
        value={values.specialOffers}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Describe any special offers, package deals, or promotional rates..."
        rows={3}
      />

      <div className="form-grid">
        <FormField
          type="select"
          name="cancellationPolicy"
          label="Cancellation Policy"
          value={values.cancellationPolicy}
          onChange={handleChange}
          onBlur={handleBlur}
          options={[
            { value: '24', label: '24 hours notice' },
            { value: '48', label: '48 hours notice' },
            { value: '72', label: '72 hours notice' }
          ]}
        />
      </div>

      <div className="step-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onPrev}
          disabled={isSubmitting}
        >
          ← Previous
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Next: Review & Complete →'}
        </button>
      </div>
    </form>
  );
};

// Step 5: Review & Complete
export const ReviewStep = ({ data, onNext, onPrev, onCancel, isLastStep }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onNext();
    } catch (error) {
      console.error('Error completing registration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="step-header">
        <h3>Review & Complete</h3>
        <p>Please review your information before completing the registration</p>
      </div>

      <div className="review-sections">
        <div className="review-section">
          <h4>Basic Information</h4>
          <div className="review-item">
            <span className="label">Name:</span>
            <span className="value">{data.title} {data.fullName}</span>
          </div>
          <div className="review-item">
            <span className="label">Email:</span>
            <span className="value">{data.email}</span>
          </div>
          <div className="review-item">
            <span className="label">Phone:</span>
            <span className="value">{data.phone}</span>
          </div>
          {data.dateOfBirth && (
            <div className="review-item">
              <span className="label">Date of Birth:</span>
              <span className="value">{new Date(data.dateOfBirth).toLocaleDateString()}</span>
            </div>
          )}
          {data.address && (
            <div className="review-item">
              <span className="label">Address:</span>
              <span className="value">{data.address}</span>
            </div>
          )}
        </div>

        <div className="review-section">
          <h4>Professional Details</h4>
          <div className="review-item">
            <span className="label">Specialization:</span>
            <span className="value">{data.specialization}</span>
          </div>
          <div className="review-item">
            <span className="label">Experience:</span>
            <span className="value">{data.experience} years</span>
          </div>
          <div className="review-item">
            <span className="label">License:</span>
            <span className="value">{data.licenseNumber}</span>
          </div>
          {data.qualifications && (
            <div className="review-item">
              <span className="label">Qualifications:</span>
              <span className="value">{data.qualifications}</span>
            </div>
          )}
          {data.bio && (
            <div className="review-item">
              <span className="label">Bio:</span>
              <span className="value">{data.bio}</span>
            </div>
          )}
        </div>

        <div className="review-section">
          <h4>Schedule & Availability</h4>
          <div className="review-item">
            <span className="label">Working Days:</span>
            <span className="value">
              {data.workingDays && data.workingDays.length > 0 
                ? data.workingDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')
                : 'Not specified'
              }
            </span>
          </div>
          <div className="review-item">
            <span className="label">Working Hours:</span>
            <span className="value">{data.startTime} - {data.endTime}</span>
          </div>
          <div className="review-item">
            <span className="label">Lunch Break:</span>
            <span className="value">{data.lunchBreakStart} - {data.lunchBreakEnd}</span>
          </div>
          <div className="review-item">
            <span className="label">Consultation Duration:</span>
            <span className="value">{data.consultationDuration} minutes</span>
          </div>
          <div className="review-item">
            <span className="label">Locations:</span>
            <span className="value">
              {data.locationIds && data.locationIds.length > 0
                ? `${data.locationIds.length} location${data.locationIds.length !== 1 ? 's' : ''} selected`
                : 'None selected'
              }
            </span>
          </div>
          <div className="review-item">
            <span className="label">Available From:</span>
            <span className="value">
              {data.availableFromDate 
                ? new Date(data.availableFromDate).toLocaleDateString()
                : 'Not specified'
              }
            </span>
          </div>
        </div>

        <div className="review-section">
          <h4>Services & Pricing</h4>
          {data.services && data.services.length > 0 ? (
            data.services.map(service => (
              <div key={service.id} className="review-item">
                <span className="label">{service.name}:</span>
                <span className="value">£{service.price} ({service.duration} min)</span>
              </div>
            ))
          ) : (
            <div className="review-item">
              <span className="label">Services:</span>
              <span className="value">No services selected</span>
            </div>
          )}
          
          {data.specialOffers && (
            <div className="review-item">
              <span className="label">Special Offers:</span>
              <span className="value">{data.specialOffers}</span>
            </div>
          )}
          
          <div className="review-item">
            <span className="label">Cancellation Policy:</span>
            <span className="value">{data.cancellationPolicy} hours notice required</span>
          </div>
        </div>
      </div>

      <div className="step-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onPrev}
          disabled={isSubmitting}
        >
          ← Previous
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Completing Registration...' : 'Complete Registration'}
        </button>
      </div>
    </form>
  );
};